import Alert from '@material-ui/lab/Alert'
import React from 'react'

import { getRoleFromStorage } from '../helpers/userData'
import { getTranslate } from '../localization'
import ProviderForm from '../screens/ProviderForm'
import ProviderList from '../screens/ProviderList'
import ProviderTabs from '../screens/ProviderTabs'
import { ROLE, RouteWithAccessType } from './types'

/**
 * Enforces the ADMIN-only access these routes already DECLARED but did not apply.
 *
 * `rolesListWithReadAccess: [ROLE.ADMIN]` reads like a gate and is not one:
 * `canAccessRoute` (`src/cache/auth.ts:28-34`) decides which entries reach the `<Switch>` and
 * consults `requiredFeatures` ONLY, returning true immediately when that list is empty or
 * absent. These three entries have no `requiredFeatures` at all, so every authenticated user
 * could reach the provider screens, none of which carried a guard of their own.
 *
 * Fixed at the component layer rather than by adding a `requiredFeatures` entry, because the
 * feature set is built from server-sent `credentials.features` (`auth.ts:13-15`) — naming a
 * `provider.*` feature the backend does not send would gate the screens shut for everyone,
 * including admins. Enforcing the role the table already declares needs no backend coordination.
 *
 * Mirrors `PromotionAccessGuard` in `promotionRoleManagement.tsx`, which exists for the same
 * reason.
 *
 * This is an AFFORDANCE, not the security boundary — the data behind these screens is protected
 * server-side. What it fixes is a declaration that claimed something the client never did.
 */
const ProviderAccessGuard: React.FC<{ component: JSX.Element }> = ({
  component
}) =>
  getRoleFromStorage() === ROLE.ADMIN ? (
    component
  ) : (
    <Alert severity="error">
      {getTranslate('You do not have access to providers.')}
    </Alert>
  )

const providerRoleManagement: RouteWithAccessType[] = [
  {
    route: '/provider',
    exact: true,
    rolesListWithReadAccess: [ROLE.ADMIN],
    rolesListWithWriteAccess: [ROLE.ADMIN],
    component: <ProviderAccessGuard component={<ProviderList />} />
  },
  {
    route: '/provider/:providerID/:providerCode/:page',
    exact: true,
    rolesListWithReadAccess: [ROLE.ADMIN],
    rolesListWithWriteAccess: [ROLE.ADMIN],
    tabs: [{ params: { page: 'details' } }, { params: { page: 'logo' } }],
    component: <ProviderAccessGuard component={<ProviderTabs />} />
  },
  {
    route: '/provider/create',
    exact: true,
    rolesListWithReadAccess: [ROLE.ADMIN],
    rolesListWithWriteAccess: [ROLE.ADMIN],
    component: <ProviderAccessGuard component={<ProviderForm />} />
  }
]

export default providerRoleManagement
